import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AsyncPipe, I18nPluralPipe, NgIf } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatLineModule } from '@angular/material/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { JwtLegFiClaims } from 'app/services/auth/jwt-legfi-claims.model';
import { GrowlerService } from 'app/services/growler.service';
import { NotificationsService } from 'app/services/notifications/notifications-service';
import { DatabaseNotification } from 'app/models/entities/database-notification';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { LegfiDatePipeModule } from 'app/pipes/format/legfi-date.module';
import { AvatarBlockModule } from '../../shared/avatar-block/avatar-block.module';
import { I18nMap } from '../../../../models/entities/i18n-map';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatFormFieldModule } from '@angular/material/form-field';
import { EmptyStateModule } from '../../shared/empty-state/empty-state.module';
import { LoadingListDirective } from '../../shared/directives/list.directive';
import { PreventClickDirective } from '../../shared/directives/prevent-click.directive';
import { ButtonSpacerDirective } from '../../shared/directives/button.directive';
import { ConfirmActionV2DialogComponent, ConfirmActionV2DialogInput } from '../../shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { HostListenerService } from '../../../../services/host-listener.service';
import { TruncateModule } from '../../../../pipes/format/truncate.module';

export enum NotificationContext {
    PMC = 'pmc',
    ORGANIZATION = 'org',
}

@UntilDestroy()
@Component({
    selector: 'app-notifications-list',
    templateUrl: './notifications-list.component.html',
    styleUrls: ['./notifications-list.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        AsyncPipe,
        MatIconModule,
        MatListModule,
        MatButtonModule,
        MatToolbarModule,
        MatLineModule,
        MatDialogModule,
        LegfiDatePipeModule,
        AvatarBlockModule,
        CdkFixedSizeVirtualScroll,
        CdkVirtualScrollViewport,
        CdkVirtualForOf,
        MatFormFieldModule,
        EmptyStateModule,
        LoadingListDirective,
        RouterLink,
        PreventClickDirective,
        ButtonSpacerDirective,
        TruncateModule,
    ], providers: [
        I18nPluralPipe,
    ],
})
export class NotificationsListComponent implements OnInit
{
    @Input() context = NotificationContext.PMC;
    @Input() isDashboardCard = false;

    @Output() notificationNavigation = new EventEmitter<void>();
    @Output() notificationsLoaded = new EventEmitter<boolean>(false);
    @Output() workingStateUpdated = new EventEmitter<boolean>(false);

    loadingInitialData = true;
    isDeleting = false;
    jwt: JwtLegFiClaims;

    viewportHeight = 200;
    viewportWidth = 200;

    readonly notificationsMapping: I18nMap = {
        '=0': 'No notifications',
        '=1': '1 notification',
        'other': '# notifications'
    };
    readonly notificationsZeroMappingOverride: I18nMap = {
        '=0': 'All notifications',
    };
    readonly notificationContexts = NotificationContext;
    readonly showEmptyState$  = new BehaviorSubject<boolean>(false);

    constructor(
            private _router: Router,
            private _notificationService: NotificationsService,
            private _host: HostListenerService,
            private _growler: GrowlerService,
            private _dialog: MatDialog,
            private _i18n: I18nPluralPipe,
            private _el: ElementRef,
    ) {
        this._notificationService.notifications$.pipe(untilDestroyed(this)).subscribe({
            next: (notifications) => {
                this.showEmptyState$.next(notifications.length === 0);
                this.updateViewport();
            },
        });
    }

    get notifications$() {
        return this._notificationService.notifications$.asObservable();
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    ngOnInit(): void {
        this._notificationService.getNotifications().pipe(
                finalize(() => {
                    // only update flag...list data is set from notification service subject
                    this.loadingInitialData = false;
                    this.notificationsLoaded.emit(true);
                }),
                untilDestroyed(this),
        ).subscribe({
            next: () => this.updateViewport(),
            error: () => this._growler.oops('Unable to fetch notifications'),
        });

        fromEvent(window, 'resize').pipe(untilDestroyed(this)).subscribe(() => {
            this.updateViewport();
        });
    }

    handleNotificationClick(notification: DatabaseNotification) {
        if (!notification.readAt) {
            return this.markAsRead([notification.id], true);
        }

        if (this.context === NotificationContext.PMC && notification.data.hasOwnProperty('pmcLink')) {
            this._router.navigate([notification.data.pmcLink, 'details']).then(() => this.notificationNavigation.emit());
        } else if (this.context === NotificationContext.ORGANIZATION && notification.data.hasOwnProperty('orgLink')) {
            this._router.navigate([notification.data.orgLink, 'details']).then(() => this.notificationNavigation.emit());
        }
    }

    markAsRead(notificationIds: string[], routeAfterSave = false) {
        const mapping = { ...this.notificationsMapping, ...this.notificationsZeroMappingOverride };
        const context = this._i18n.transform(notificationIds.length, mapping);

        this.workingStateUpdated.emit(true);
        this._notificationService.markNotificationAsRead(notificationIds).pipe(
                finalize(() => this.workingStateUpdated.emit(false)),
                untilDestroyed(this),
        ).subscribe({
            next: (notifications) => {
                if (routeAfterSave) {
                    this.handleNotificationClick(notifications[0]);
                } else {
                    this._growler.success('Success', `${context} marked as read`);
                }
            },
            error: () => this._growler.oops(`Unable to mark ${context} as read`),
        });
    }

    deleteNotifications(notificationIds: string[]) {
        const mapping = { ...this.notificationsMapping, ...this.notificationsZeroMappingOverride };
        const context = this._i18n.transform(notificationIds.length, mapping).toLowerCase();

        const data: ConfirmActionV2DialogInput = {
            title: `Delete ${context}?`,
            content: 'This action cannot be undone.',
            confirmButton: `Yes, delete ${context}`,
            cancelButton: 'No',
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe({
            next: (res) => {
                if (res?.success) {
                    this.workingStateUpdated.emit(true);
                    this.isDeleting = true;
                    this._notificationService.deleteNotifications(notificationIds).pipe(
                            finalize(() => {
                                this.workingStateUpdated.emit(false);
                                this.isDeleting = false;
                            }),
                            untilDestroyed(this),
                    ).subscribe({
                        next: () => this._growler.success('Success', `${context} deleted`),
                        error: () => this._growler.oops(`Unable to delete ${context}`),
                    });
                }
            }
        });
    }

    private updateViewport() {
        this.viewportHeight = this.showEmptyState$.value ?  0 : this._el.nativeElement.clientHeight - 24;
        this.viewportWidth = this._el.nativeElement.clientWidth - 15;
    }
}
